// components/ConnectEpicButton.js
//
// Drop this into both:
//   1. The patient dashboard/profile page
//   2. Care Copilot's "My Profile" mode
//
// Usage: <ConnectEpicButton />
// Optional: <ConnectEpicButton style={{ marginTop: 12 }} />
//
// FEATURE FLAG: set REACT_APP_ENABLE_EPIC=false in your build environment
// (e.g. this specific Codemagic build for the urgent iOS fix) to hide this
// component entirely — renders null immediately, no Epic UI shows up
// anywhere it's used, without touching any of the pages that embed it.
// Default is enabled — omit the env var entirely for normal builds.

import React, { useState } from 'react';
import { useEpicConnect } from '../hooks/useEpicConnect';

const EPIC_ENABLED = process.env.REACT_APP_ENABLE_EPIC !== 'false';

const ConnectEpicButton = ({ style = {} }) => {
  const { connected, checking, connecting, syncing, connectEpic, syncNow } = useEpicConnect();
  const [lastSyncResult, setLastSyncResult] = useState(null);

  if (!EPIC_ENABLED) return null;
  if (checking) return null;

  if (connected) {
    const handleSyncNow = async () => {
      setLastSyncResult(null);
      try {
        const result = await syncNow();
        setLastSyncResult({
          ok: true,
          text: `Synced: ${result?.labsImported ?? 0} labs, ${result?.conditionsImported ?? 0} conditions`,
        });
      } catch (e) {
        const backendMessage = e.response?.data?.error;
        setLastSyncResult({
          ok: false,
          text: backendMessage || 'Sync failed — patient may not have an active Epic connection',
        });
      }
      setTimeout(() => setLastSyncResult(null), 8000);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 10,
              background: 'rgba(16,185,129,0.12)',
              border: '1px solid rgba(16,185,129,0.3)',
              fontSize: 12.5,
              fontWeight: 600,
              color: '#10b981',
              fontFamily: "'DM Sans', system-ui, sans-serif",
            }}
          >
            ✅ Health records connected
          </div>

          <button
            onClick={handleSyncNow}
            disabled={syncing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: 10,
              background: syncing ? 'rgba(102,126,234,0.15)' : 'rgba(102,126,234,0.1)',
              border: '1px solid rgba(102,126,234,0.3)',
              color: '#1A5EA8',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: syncing ? 'not-allowed' : 'pointer',
              fontFamily: "'DM Sans', system-ui, sans-serif",
            }}
          >
            {syncing ? '🔄 Syncing…' : '🔄 Sync Now'}
          </button>
        </div>

        {lastSyncResult && (
          <div
            style={{
              fontSize: 12,
              color: lastSyncResult.ok ? '#10b981' : '#ef4444',
              fontFamily: "'DM Sans', system-ui, sans-serif",
            }}
          >
            {lastSyncResult.text}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={connectEpic}
      disabled={connecting}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 18px',
        borderRadius: 10,
        background: connecting
          ? 'rgba(102,126,234,0.4)'
          : 'linear-gradient(135deg,#1A5EA8,#0B2545)',
        color: 'white',
        border: 'none',
        fontSize: 13,
        fontWeight: 700,
        cursor: connecting ? 'not-allowed' : 'pointer',
        fontFamily: "'DM Sans', system-ui, sans-serif",
        boxShadow: connecting ? 'none' : '0 4px 16px rgba(102,126,234,0.35)',
        transition: 'all 0.2s',
        ...style,
      }}
    >
      {connecting ? 'Connecting…' : '🏥 Connect My Health Records'}
    </button>
  );
};

export default ConnectEpicButton;